/**
 * Manages the global loading state and provides a context for loading state management
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { Loading } from '@ankor-io/ui-components/src/Loading'

import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { getPathInfo } from '@/utils/routeUtils'

import { LoadingStateContext, type LoadingStateContextValue } from './LoadingStateContext'

interface GlobalLoadingProviderProps {
  children: ReactNode
}

export function GlobalLoadingProvider({ children }: GlobalLoadingProviderProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false)

  const pathInfo = useMemo(() => getPathInfo(), [])

  // Check if any providers loading states are active
  const isAnyLoading = useMemo(() => {
    return Object.values(loadingStates).some((isLoading) => isLoading)
  }, [loadingStates])

  const isProviderLoading = Object.keys(loadingStates).length > 0

  // Mark initial load as complete once providers finish loading
  useEffect(() => {
    if (isProviderLoading && !isAnyLoading && !hasCompletedInitialLoad) {
      setHasCompletedInitialLoad(true)
    }
  }, [isProviderLoading, isAnyLoading, hasCompletedInitialLoad])

  const shouldShowLoader = !hasCompletedInitialLoad || isAnyLoading

  const FORCED_LOAD_TIME_MS = 2500
  const showLoaderWithMinTime = useMinimumDisplayTime(shouldShowLoader, FORCED_LOAD_TIME_MS)

  // Don't show loader on unauthenticated routes or /auth/callback
  const showLoader = pathInfo.isUnauthenticatedRoute || pathInfo.isAuthCallback ? false : showLoaderWithMinTime

  // Block Router when showing loader
  const isGlobalLoaderActive = showLoader

  const registerLoadingState = useCallback((name: string, isLoading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [name]: isLoading,
    }))
  }, [])

  const unregisterLoadingState = useCallback((name: string) => {
    setLoadingStates((prev) => {
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])

  const contextValue: LoadingStateContextValue = useMemo(
    () => ({
      registerLoadingState,
      unregisterLoadingState,
      isLoading: isAnyLoading,
      isGlobalLoaderActive,
    }),
    [registerLoadingState, unregisterLoadingState, isAnyLoading, isGlobalLoaderActive],
  )

  // Determine background color based on theme class
  // Use inline style to ensure it's applied immediately, even before Tailwind processes
  const getBackgroundColor = () => {
    try {
      if (typeof document !== 'undefined' && document.documentElement) {
        const isDark = document.documentElement.classList.contains('dark')
        return isDark ? 'rgb(17, 24, 39)' : 'rgb(255, 255, 255)' // gray-900 or white
      }
    } catch {
      // Fall through to SSR fallback if document access fails
    }

    // Fallback during SSR or if document is not available
    return 'rgb(17, 24, 39)' // Default to dark
  }

  return (
    <LoadingStateContext.Provider value={contextValue}>
      {showLoader && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center transition-colors duration-200"
          style={{ backgroundColor: getBackgroundColor() }}
        >
          <Loading
            variant="logo"
            size="lg"
          />
        </div>
      )}
      {/* 
        Always render children so providers can mount and register their loading states.
        The DeferredRouter component (used in main.tsx) prevents Router and all pages from
        rendering until the global loader completes, ensuring pages don't mount and make
        HTTP requests during the animation or start painting the page.
        This gives the animation full CPU/GPU resources for a very smooth UX.
      */}
      {children}
    </LoadingStateContext.Provider>
  )
}
